import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type IpMode = 'v4' | 'v6';

interface State {
    mode: IpMode;
    count: number;
    cidr: string;
    output: string;
    error: string | null;
    copied: boolean;
}

function parseV4Cidr(cidr: string): { base: number; bits: number } | null {
    const m = cidr.trim().match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/);
    if (!m) return null;
    const o = m.slice(1, 5).map(Number);
    const bits = Number(m[5]);
    if (o.some(x => x > 255) || bits < 0 || bits > 32) return null;
    const base = (o[0] << 24 >>> 0) + (o[1] << 16) + (o[2] << 8) + o[3];
    return { base, bits };
}

function v4Random(base: number, bits: number): string {
    const hostBits = 32 - bits;
    const mask = hostBits === 32 ? 0xffffffff : ((1 << hostBits) - 1);
    const masked = (base >>> 0) & ~mask >>> 0;
    const offset = Math.floor(Math.random() * (1 << Math.min(hostBits, 30)));
    const ip = (masked + offset) >>> 0;
    return `${(ip >>> 24) & 0xff}.${(ip >>> 16) & 0xff}.${(ip >>> 8) & 0xff}.${ip & 0xff}`;
}

function v4Free(): string {
    const a = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    const c = Math.floor(Math.random() * 256);
    const d = Math.floor(Math.random() * 256);
    return `${a}.${b}.${c}.${d}`;
}

function v6Random(): string {
    const parts: string[] = [];
    for (let i = 0; i < 8; i++) {
        parts.push(Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0'));
    }
    return parts.join(':');
}

export default class RandomIpComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = { mode: 'v4', count: 10, cidr: '', output: '', error: null, copied: false };

    componentDidMount() {
        this.generate();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    generate = () => {
        const ips: string[] = [];
        if (this.state.mode === 'v4') {
            if (this.state.cidr.trim()) {
                const parsed = parseV4Cidr(this.state.cidr);
                if (!parsed) {
                    this.setState({ error: 'Invalid IPv4 CIDR (e.g. 10.0.0.0/24)', output: '' });
                    return;
                }
                for (let i = 0; i < this.state.count; i++) ips.push(v4Random(parsed.base, parsed.bits));
            } else {
                for (let i = 0; i < this.state.count; i++) ips.push(v4Free());
            }
        } else {
            for (let i = 0; i < this.state.count; i++) ips.push(v6Random());
        }
        this.setState({ output: ips.join('\n'), error: null, copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { mode, count, cidr, output, error, copied } = this.state;
        const ipCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button className={`${genStyles.iconBtn} ${mode === 'v4' ? genStyles.iconBtnPrimary : ''}`}
                        onClick={() => this.setState({ mode: 'v4' }, this.generate)}>IPv4</button>
                    <button className={`${genStyles.iconBtn} ${mode === 'v6' ? genStyles.iconBtnPrimary : ''}`}
                        onClick={() => this.setState({ mode: 'v6' }, this.generate)}>IPv6</button>
                </div>
                {mode === 'v4' && (
                    <div className={genStyles.field}>
                        <label htmlFor="rip-cidr">CIDR range (optional)</label>
                        <input id="rip-cidr" type="text" value={cidr} placeholder="e.g. 10.0.0.0/24 (blank = full range)"
                            onChange={e => this.setState({ cidr: e.target.value }, this.generate)} />
                    </div>
                )}
                <div className={genStyles.field}>
                    <label htmlFor="rip-count">Number of IPs</label>
                    <input id="rip-count" type="number" inputMode="decimal" min={1} max={10000} value={count}
                        onChange={e => this.setState({ count: Math.max(1, Math.min(10000, +e.target.value || 10)) }, this.generate)} />
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate} style={{ alignSelf: 'flex-start' }}>
                    ↻ Regenerate
                </button>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel={`Generated ${mode === 'v4' ? 'IPv4' : 'IPv6'} addresses`}
                previewMeta={`${ipCount} IP${ipCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated IP addresses">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
                error={error}
            />
        );
    }
}
